import PropTypes from "prop-types";
import React from "react";
import { ArrowRight, Button, ProgressBar, Table, Tooltip } from "@opendate-app/setlist";
import LineupCard from "./LineupCard";
import LinksCard from "./LinksCard";

const DAY_MS = 24 * 60 * 60 * 1000;

const fmtCurrency = (n) => `${n < 0 ? "−" : ""}$${Math.abs(Math.round(n)).toLocaleString("en-US")}`;
const fmtCents = (n) => `$${n.toFixed(2)}`;
const fmtNumber = (n) => Math.round(n).toLocaleString("en-US");
const fmtPct = (n) => `${Number(n.toFixed(1))}%`;
const fmtShortDate = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });

const parseSeriesDate = (s) => {
  const [m, d, y] = s.split("/").map(Number);
  return new Date(2000 + y, m - 1, d);
};

const daysBetween = (from, to) => Math.round((to - from) / DAY_MS);
const addDays = (date, days) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

export const weeklySales = (salesSeries) => {
  if (!salesSeries || salesSeries.length === 0) return [];
  const first = parseSeriesDate(salesSeries[0].date);
  const weeks = [];
  let prior = 0;
  salesSeries.forEach((point) => {
    const week = Math.floor(daysBetween(first, parseSeriesDate(point.date)) / 7);
    while (weeks.length <= week) weeks.push(0);
    weeks[week] += point.number_of_tickets_sold - prior;
    prior = point.number_of_tickets_sold;
  });
  return weeks;
};

const Stat = ({ label, value, sub, children }) => (
  <div className="pe-stat">
    <div className="pe-stat-label">{label}</div>
    <div className="pe-stat-value">{value}</div>
    {children}
    {sub && <div className="pe-stat-sub">{sub}</div>}
  </div>
);

Stat.propTypes = { label: PropTypes.string, value: PropTypes.node, sub: PropTypes.node, children: PropTypes.node };

export const ShowRecapCard = ({ dashboard, onSelectTab }) => {
  const { ticketsSold, capacity, sellThroughPctExact, sales, addOnBreakdown, settlement } = dashboard;
  const addOnsSold = addOnBreakdown.reduce((sum, a) => sum + a.sold, 0);
  const perHead = (amount) => (ticketsSold > 0 ? amount / ticketsSold : 0);

  return (
    <div className="d2-card pe-recap">
      <div className="pe-recap-head">
        <div>
          <h2 className="pe-title">Show recap</h2>
          <p className="pe-sub">Final numbers for this show.</p>
        </div>
        {settlement && <Button variant="secondary" size="sm" onClick={() => onSelectTab("settlements")}>View settlement</Button>}
      </div>
      <div className="pe-stat-grid">
        <Stat label="Tickets sold" value={fmtNumber(ticketsSold)} sub={capacity > 0 ? `of ${fmtNumber(capacity)} capacity` : null} />
        <Stat label="Capacity sold" value={fmtPct(sellThroughPctExact)} sub={capacity > 0 ? `${fmtNumber(Math.max(capacity - ticketsSold, 0))} tickets unsold` : null}>
          <ProgressBar value={Math.min(Math.round(sellThroughPctExact), 100)} label="none" />
        </Stat>
        {settlement && (
          <Stat
            label="Total profit"
            value={fmtCurrency(settlement.netProfit)}
            sub={settlement.grossRevenue > 0 ? `${fmtPct((settlement.netProfit / settlement.grossRevenue) * 100)} margin on ${fmtCurrency(settlement.grossRevenue)} gross` : null}
          />
        )}
        <Stat label="Gross ticket sales" value={fmtCurrency(sales.tickets)} sub={ticketsSold > 0 ? `Avg ${fmtCents(perHead(sales.tickets))} per ticket` : null} />
        <Stat label="Gross add-on sales" value={fmtCurrency(sales.addOns)} sub={`${fmtNumber(addOnsSold)} add-ons · ${fmtCents(perHead(sales.addOns))} per head`} />
        {settlement && settlement.grossFoodAndBeverage > 0 && (
          <Stat label="Gross F&B" value={fmtCurrency(settlement.grossFoodAndBeverage)} sub={`${fmtCents(perHead(settlement.grossFoodAndBeverage))} per head`} />
        )}
      </div>
    </div>
  );
};

ShowRecapCard.propTypes = { dashboard: PropTypes.object.isRequired, onSelectTab: PropTypes.func.isRequired };

export const SalesPaceCard = ({ salesSeries, startTime }) => {
  const weeks = weeklySales(salesSeries);
  if (weeks.length === 0) return null;

  const firstSale = parseSeriesDate(salesSeries[0].date);
  const showDay = startTime ? new Date(startTime) : parseSeriesDate(salesSeries[salesSeries.length - 1].date);
  const peak = Math.max(...weeks);
  const peakWeek = weeks.indexOf(peak);
  const daysOnSale = Math.max(daysBetween(firstSale, showDay), 0);

  return (
    <div className="d2-card">
      <div className="pe-recap-head">
        <div>
          <h2 className="pe-title">Sales pace</h2>
          <p className="pe-sub">Tickets sold per week, first sale to show day</p>
        </div>
        <div className="pe-pace-kpis">
          <div><div className="pe-pace-num">{fmtNumber(daysOnSale)}</div><div className="pe-stat-sub">Days on sale</div></div>
          <div><div className="pe-pace-num">{fmtNumber(peak)}</div><div className="pe-stat-sub">Peak week · Wk {peakWeek + 1}</div></div>
        </div>
      </div>
      <div className="pe-pace-bars" role="img" aria-label={`Weekly ticket sales over ${weeks.length} weeks, peaking at ${peak} in week ${peakWeek + 1}`}>
        {weeks.map((count, i) => {
          const weekStart = addDays(firstSale, i * 7);
          const weekEnd = addDays(weekStart, 6);
          return (
            <Tooltip
              key={i}
              title={`${fmtNumber(count)} ${count === 1 ? "ticket" : "tickets"} sold`}
              supportingText={`Week ${i + 1} · ${fmtShortDate(weekStart)} – ${fmtShortDate(weekEnd)}`}
            >
              <div
                className={`pe-pace-bar${i === peakWeek || i === weeks.length - 1 ? " is-strong" : ""}`}
                style={{ height: `${peak > 0 ? Math.max((count / peak) * 100, 2) : 2}%` }}
              />
            </Tooltip>
          );
        })}
      </div>
      <div className="pe-pace-axis">
        <span>{fmtShortDate(firstSale)} · First sale</span>
        <span>{fmtShortDate(showDay)} · Show day</span>
      </div>
    </div>
  );
};

SalesPaceCard.propTypes = { salesSeries: PropTypes.array, startTime: PropTypes.string };

export const TicketTypesCard = ({ ticketBreakdown, ticketsSold, capacity, onSelectTab }) => {
  const totalGross = ticketBreakdown.reduce((sum, t) => sum + t.revenue, 0);
  const totalSold = ticketBreakdown.reduce((sum, t) => sum + t.sold, 0);
  const rows = ticketBreakdown.length > 0
    ? [...[...ticketBreakdown].sort((a, b) => b.sold - a.sold), {
      id: "total",
      isTotal: true,
      name: "Total",
      sold: totalSold,
      revenue: totalGross,
      avg: totalSold > 0 ? totalGross / totalSold : 0,
      sellThroughPct: capacity > 0 ? (ticketsSold * 100) / capacity : 0,
    }]
    : [];
  const strong = (row, text) => (row.isTotal ? <strong>{text}</strong> : text);

  const columns = [
    { key: "name", header: "Ticket type", primary: true, render: (t) => <strong>{t.name}</strong> },
    { key: "price", header: "Price", align: "right", render: (t) => (t.isTotal ? strong(t, `Avg ${fmtCents(t.avg)}`) : fmtCents(t.price)) },
    { key: "sold", header: "Sold", align: "right", render: (t) => strong(t, fmtNumber(t.sold)) },
    { key: "gross", header: "Gross", align: "right", render: (t) => strong(t, fmtCurrency(t.revenue)) },
    { key: "sellThrough", header: "Sell-through", align: "right", render: (t) => strong(t, fmtPct(t.sellThroughPct)) },
  ];

  return (
    <div className="d2-card">
      <div className="d2-card-header">
        <span className="d2-card-title">Ticket types</span>
        <Button variant="link-color" size="sm" trailingIcon={ArrowRight} onClick={() => onSelectTab("tickets")}>All ticket details</Button>
      </div>
      <Table
        columns={columns}
        data={rows}
        getRowKey={(t) => t.id}
        empty={<p className="pe-sub">No ticket types.</p>}
      />
    </div>
  );
};

TicketTypesCard.propTypes = {
  ticketBreakdown: PropTypes.array.isRequired,
  ticketsSold: PropTypes.number,
  capacity: PropTypes.number,
  onSelectTab: PropTypes.func.isRequired,
};

export const SettlementCard = ({ settlement }) => (
  <div className="d2-card">
    <div className="d2-card-header"><span className="d2-card-title">Settlement</span></div>
    <dl className="pe-settlement">
      <div><dt>Gross revenue</dt><dd>{fmtCurrency(settlement.grossRevenue)}</dd></div>
      <div><dt>Artist payout</dt><dd>{fmtCurrency(-settlement.artistPayout)}</dd></div>
      <div><dt>Show expenses</dt><dd>{fmtCurrency(-settlement.otherExpenses)}</dd></div>
      <div className="is-total"><dt>Net profit</dt><dd>{fmtCurrency(settlement.netProfit)}</dd></div>
    </dl>
  </div>
);

SettlementCard.propTypes = { settlement: PropTypes.object.isRequired };

const PastEventDashboard = ({ dashboard, lineup, showLineup, links, startTime, teamId, confirmId, csrfToken, onWebsiteChange, onSelectTab, artistPicker }) => (
  <div className="d2-grid-ready">
    <div className="d2-ready-rail">
      <ShowRecapCard dashboard={dashboard} onSelectTab={onSelectTab} />
      <SalesPaceCard salesSeries={dashboard.salesSeries} startTime={startTime} />
      <TicketTypesCard ticketBreakdown={dashboard.ticketBreakdown} ticketsSold={dashboard.ticketsSold} capacity={dashboard.capacity} onSelectTab={onSelectTab} />
    </div>
    <div className="d2-ready-rail">
      {dashboard.settlement && <SettlementCard settlement={dashboard.settlement} />}
      {showLineup && (
        <LineupCard
          lineup={lineup}
          canManage={artistPicker.enabled}
          busyId={artistPicker.busyId}
          onAddArtist={artistPicker.openAdd}
          onEditArtist={artistPicker.openEdit}
          onRemoveArtist={artistPicker.remove}
        />
      )}
      {links && <LinksCard links={links} teamId={teamId} confirmId={confirmId} csrfToken={csrfToken} onWebsiteChange={onWebsiteChange} />}
    </div>
  </div>
);

PastEventDashboard.propTypes = {
  dashboard: PropTypes.object.isRequired,
  lineup: PropTypes.array.isRequired,
  showLineup: PropTypes.bool,
  links: PropTypes.object,
  startTime: PropTypes.string,
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  confirmId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  csrfToken: PropTypes.string,
  onWebsiteChange: PropTypes.func,
  onSelectTab: PropTypes.func.isRequired,
  artistPicker: PropTypes.object.isRequired,
};

export default PastEventDashboard;
